<template>
  <div class="khub">
    <!-- Loading ITW mientras se crea el job y se hace polling -->
    <LoadingModel
      :visible="loading"
      :message="khMensaje"
      :progress="khProgreso"
    />

    <!-- Encabezado tipo IAModel, en rojo -->
    <!-- <v-sheet class="pa-4 w-100 mt-3" color="#c1121f" elevation="2" rounded>
      <h1 class="text-h4 font-weight-bold d-flex align-center text-white">
        <v-btn icon variant="text" color="white">
          <v-icon>mdi-book-open-variant</v-icon>
        </v-btn>
        Knowledge Hub (RAG)
      </h1>
    </v-sheet> -->

    <!-- Card principal (mismo estilo que IAModel) -->
    <v-card elevation="2" class="pa-4 mt-5">
      <v-card-title class="bg-grey-lighten-4 text-center">
        Procesar Knowledge Hub
      </v-card-title>
      <v-card-text>
        <div class="mb-4">
          <small class="grey--text text--darken-1">
            * Envía la transcripción al Knowledge Hub y espera a que termine el
            procesamiento (puede tardar unos minutos).
          </small>
        </div>

        <div class="d-flex justify-center mb-2">
          <v-btn
            color="#c1121f"
            size="large"
            class="text-none"
            :loading="loading"
            :disabled="loading || !projectId"
            @click.prevent="procesar"
          >
            <v-icon start>mdi-cog-play-outline</v-icon>
            Procesar Knowledge Hub
          </v-btn>
        </div>

        <v-alert
          v-if="estado"
          :type="alertType"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ estadoMensaje }}
        </v-alert>

      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { getJobKnowledge, getStatus, getKnowledgeResult } from '@/api/KnowledgeService';
import LoadingModel from "@/components/Global/Loading.vue";
import { celebrar, swal } from "@/utils/swal.js";
import { useSettingsStore } from "@/stores/settings";


export default {
  name: "KnowledgeHub",
  components: {
    LoadingModel,
  },
  props: {
    // ID del proyecto para el que se procesa el Knowledge Hub.
    projectId: { type: [String, Number], default: null },
    description: {type: [String, Number], default:null},
    // Datos extra opcionales que se mezclan en el body del POST.
    extraPayload: { type: Object, default: () => ({}) },
  },
  emits: ["done", "error"],
  data() {
    return {
      loading: false,
      estado: null, // "processing" | "done" | "error"
      jobId: null,
      // Progreso / etapa / cola / detalle del status
      khProgreso: null,
      khEtapa: "",
      khQueue: null,
      khDetalle: "",
    };
  },
  computed: {
    isDark() {
      return useSettingsStore().theme === "dark";
    },
    // Mensaje del loading segun el estado del job.
    khMensaje() {
      if (this.khQueue) {
        const occ = this.khQueue.occupied_workers ?? "?";
        const max = this.khQueue.max_workers ?? "?";
        return `En cola — ${occ} de ${max} procesos ocupados`;
      }
      if (this.khEtapa) return this.khEtapa; // ej "Generando entregables"
      if (this.khProgreso != null) return "Procesando…";
      return "KnowledgeHub…";
    },
    alertType() {
      if (this.estado === "done") return "success";
      if (this.estado === "error") return "error";
      return "info";
    },
    estadoMensaje() {
      if (this.estado === "processing")
        return "Procesando en el Knowledge Hub…";
      if (this.estado === "done")
        return "Knowledge Hub procesado correctamente.";
      if (this.estado === "error")
        return "Hubo un error al procesar el Knowledge Hub.";
      return "";
    },
  },
  methods: {
     async mostrarConfeti() {
      await celebrar({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    },
    async procesar() {
      this.loading = true;
      this.estado = "processing";
      this.jobId = null;
      this.khProgreso = null;
      this.khEtapa = "";
      this.khQueue = null;
      this.khDetalle = "";
      try {
        // 1) Crear el job: responde al instante con un id.
        console.log("fff", this.extraPayload )
        const body = {
          project: this.projectId,
          description: this.description,
          ...this.extraPayload,
        };
        const jobId = await getJobKnowledge(body);

        if (!jobId) {
          throw new Error("El servidor no devolvió un id de Knowledge Hub");
        }
        // Guardamos el id: se usa para construir las URLs de descarga.
        this.jobId = jobId;

        // 2) Polling del estado hasta terminar (no mantiene una petición larga).
        const data = await this.pollStatus(jobId);
        // Ya termino: apagamos progreso/etapa/cola (el detalle se conserva).
        this.khProgreso = null;
        this.khEtapa = "";
        this.khQueue = null;

        // 3) "puro resultado": test_cases / user_stories (mismo formato que el
        //    modelo IA). Los ARTEFACTOS NO vienen aqui, sino del job (status).
        let result = data;
        await this.mostrarConfeti();
        try {
          result = await getKnowledgeResult(jobId);
          console.log("KnowledgeHub result →", result);
        } catch (e) {
          console.warn("No se pudo obtener el result del Knowledge Hub", e);
        }

        // Artefactos del job (status/pollStatus data), no del result.
        const artifacts = data?.artifacts ?? data?.data?.artifacts ?? [];
        console.log("KnowledgeHub artifacts →", artifacts);

        this.estado = "done";
        // casos/historias del result + artefactos del job + jobId.
        this.$emit("done", {
          test_cases: result?.test_cases,
          user_stories: result?.user_stories,
          artifacts,
          jobId,
        });
      } catch (err) {
        console.error(err);
        this.estado = "error";

        // 402 (sin creditos) / 409 (sin configuracion o no disponible):
        // avisamos que ya no tiene disponible para procesar.
        const status = err?.response?.status;
        if (status === 402 || status === 409) {
          await swal({
            icon: "warning",
            title: "Creditos Insuficientes",
            text:
              err?.response?.data?.error ||
              err?.response?.data?.detail ||
              "Ya no tienes disponible para procesar el Knowledge Hub.",
            customClass: {
              popup: this.isDark ? "mi-popup" : "mi-popup-2",
              title: "mi-titulo",
            },
          });
        }

        this.$emit("error", err);
      } finally {
        this.loading = false;
      }
    },

    // Consulta el estado del job hasta que termine (done) o falle (error).
    // maxWaitMs = 1 hora: el KnowledgeHub puede tardar mucho.
    // maxErroresSeguidos: un timeout/blip de red aislado NO es fatal; solo
    // cortamos si fallan varias consultas seguidas (el job sigue vivo atras).
    async pollStatus(
      id,
      { interval = 3000, maxWaitMs = 3600000, maxErroresSeguidos = 5 } = {},
    ) {
      const startedAt = Date.now();
      let erroresSeguidos = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - startedAt > maxWaitMs) {
          throw new Error(
            "El procesamiento del Knowledge Hub excedió el tiempo de espera",
          );
        }

        let data;
        try {
          data = await getStatus(id);
          erroresSeguidos = 0; // consulta OK: reiniciamos el contador.
        } catch (e) {
          // Fallo de red aislado: reintentamos en la siguiente vuelta en vez
          // de tumbar todo el proceso. Solo fallamos tras varios seguidos.
          erroresSeguidos += 1;
          console.warn(
            `KnowledgeHub: fallo al consultar estado (${erroresSeguidos}/${maxErroresSeguidos})`,
            e,
          );
          if (erroresSeguidos >= maxErroresSeguidos) {
            throw new Error(
              "No se pudo consultar el estado del Knowledge Hub tras varios intentos",
            );
          }
          await new Promise((resolve) => setTimeout(resolve, interval));
          continue;
        }

        // El detalle puede venir en cualquier estado (incluido done) → lo
        // guardamos siempre que exista para mostrarlo al usuario.
        if (data.detalle) this.khDetalle = data.detalle;

        if (data.status === "done") return data.data ?? data;
        if (data.status === "error") {
          throw new Error(data.error || "Error en el Knowledge Hub");
        }

        // pending: posicion/estado de la cola. processing: progreso + etapa.
        if (data.status === "pending") {
          this.khQueue = data.queue_state || null;
          this.khProgreso = null;
          this.khEtapa = "";
        } else if (data.status === "processing") {
          this.khProgreso = data.progress ?? null; // progress_percent
          this.khEtapa = data.etapa || "";
          this.khQueue = null;
        }

        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    },
  },
};
</script>
